if (!customElements.get('product-video-stories')) {
  class ProductVideoStories extends HTMLElement {
    constructor() {
      super();

      this.currentIndex = 0;
      this.video = null;
      this.touchStartX = 0;
      this.touchStartY = 0;

      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleDialogClick = this.handleDialogClick.bind(this);
      this.handleTimeUpdate = this.handleTimeUpdate.bind(this);
      this.handleEnded = this.handleEnded.bind(this);
    }

    connectedCallback() {
      this.storyButtons = [...this.querySelectorAll('[data-story-trigger]')];
      this.dialog = this.querySelector('[data-story-dialog]');
      this.viewer = this.querySelector('[data-story-viewer]');
      this.videoContainer = this.querySelector('[data-video-container]');
      this.titleElement = this.querySelector('[data-story-title]');
      this.avatarImage = this.querySelector('[data-story-avatar]');
      this.progressList = this.querySelector('[data-progress-list]');
      this.closeButton = this.querySelector('[data-story-close]');
      this.previousButton = this.querySelector('[data-story-previous]');
      this.nextButton = this.querySelector('[data-story-next]');
      this.soundButton = this.querySelector('[data-story-sound]');

      if (!this.dialog || this.storyButtons.length === 0) return;

      this.stories = this.storyButtons.map((button) => ({
        title: button.dataset.title || '',
        videoUrl: button.dataset.videoUrl || '',
        posterUrl: button.dataset.posterUrl || '',
        thumbnailUrl: button.dataset.thumbnailUrl || '',
      }));

      this.storyButtons.forEach((button, index) => {
        button.addEventListener('click', () => this.open(index));
      });

      this.closeButton?.addEventListener('click', () => this.close());
      this.previousButton?.addEventListener('click', () => this.previous());
      this.nextButton?.addEventListener('click', () => this.next());
      this.soundButton?.addEventListener('click', () => this.toggleSound());

      this.dialog.addEventListener('click', this.handleDialogClick);
      this.viewer.addEventListener('touchstart', (event) => {
        this.touchStartX = event.changedTouches[0].clientX;
        this.touchStartY = event.changedTouches[0].clientY;
      }, { passive: true });

      this.viewer.addEventListener('touchend', (event) => {
        this.handleSwipe(event);
      }, { passive: true });
    }

    open(index) {
      this.currentIndex = index;
      this.renderProgress();
      this.loadStory();

      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }

      document.body.classList.add('product-video-stories-open');
      document.addEventListener('keydown', this.handleKeydown);

      this.closeButton?.focus();
    }

    close() {
      this.destroyVideo();

      if (this.dialog.open && typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
      }

      document.body.classList.remove('product-video-stories-open');
      document.removeEventListener('keydown', this.handleKeydown);

      const activeStoryButton = this.storyButtons[this.currentIndex];

if (activeStoryButton) {
  activeStoryButton.blur();
}

if (document.activeElement instanceof HTMLElement) {
  document.activeElement.blur();
}
    }

    loadStory() {
      const story = this.stories[this.currentIndex];
      if (!story || !story.videoUrl) return;

      this.destroyVideo();
      this.updateProgressStates();

      this.titleElement.textContent = story.title;

      if (story.thumbnailUrl) {
        this.avatarImage.src = story.thumbnailUrl;
        this.avatarImage.alt = '';
      }

      const video = document.createElement('video');

      video.className = 'product-video-stories__video';
      video.src = story.videoUrl;
      video.poster = story.posterUrl;
      video.preload = 'metadata';
      video.playsInline = true;
      video.autoplay = true;
      video.muted = false;
      video.volume = 1;
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('aria-label', story.title);

      video.addEventListener('timeupdate', this.handleTimeUpdate);
      video.addEventListener('ended', this.handleEnded);

      this.videoContainer.replaceChildren(video);
      this.video = video;

      this.soundButton?.setAttribute('aria-pressed', 'false');

      const playPromise = video.play();

      if (playPromise !== undefined) {
        playPromise.catch(() => {
          /*
           * Certains navigateurs peuvent malgré le clic refuser
           * exceptionnellement le démarrage avec le son.
           * Dans ce cas, on lance la vidéo en mode muet plutôt
           * que de bloquer complètement la lecture.
           */
          video.muted = true;
          this.soundButton?.setAttribute('aria-pressed', 'true');

          video.play().catch(() => {});
        });
      }
    }

    destroyVideo() {
      if (!this.video) {
        this.videoContainer?.replaceChildren();
        return;
      }

      this.video.pause();
      this.video.removeEventListener('timeupdate', this.handleTimeUpdate);
      this.video.removeEventListener('ended', this.handleEnded);
      this.video.removeAttribute('src');
      this.video.load();
      this.video.remove();
      this.video = null;
    }

    renderProgress() {
      this.progressList.innerHTML = '';

      this.stories.forEach(() => {
        const progress = document.createElement('span');
        progress.className = 'product-video-stories__progress';

        const fill = document.createElement('span');
        fill.className = 'product-video-stories__progress-fill';

        progress.appendChild(fill);
        this.progressList.appendChild(progress);
      });

      this.progressList.style.setProperty('--story-count', this.stories.length);
    }

    updateProgressStates() {
      const fills = [...this.progressList.querySelectorAll(
        '.product-video-stories__progress-fill'
      )];

      fills.forEach((fill, index) => {
        if (index < this.currentIndex) {
          fill.style.transform = 'scaleX(1)';
        } else {
          fill.style.transform = 'scaleX(0)';
        }
      });
    }

    handleTimeUpdate() {
      if (!this.video || !Number.isFinite(this.video.duration)) return;

      const progress = this.video.currentTime / this.video.duration;
      const fills = this.progressList.querySelectorAll(
        '.product-video-stories__progress-fill'
      );

      const currentFill = fills[this.currentIndex];

      if (currentFill) {
        currentFill.style.transform = `scaleX(${Math.min(progress, 1)})`;
      }
    }

    handleEnded() {
      if (this.currentIndex < this.stories.length - 1) {
        this.next();
      } else {
        this.close();
      }
    }

    previous() {
      if (this.video && this.video.currentTime > 2) {
        this.video.currentTime = 0;
        return;
      }

      if (this.currentIndex > 0) {
        this.currentIndex -= 1;
        this.loadStory();
      } else if (this.video) {
        this.video.currentTime = 0;
      }
    }

    next() {
      if (this.currentIndex < this.stories.length - 1) {
        this.currentIndex += 1;
        this.loadStory();
      } else {
        this.close();
      }
    }

    toggleSound() {
      if (!this.video) return;

      this.video.muted = !this.video.muted;

      this.soundButton?.setAttribute(
        'aria-pressed',
        this.video.muted ? 'true' : 'false'
      );
    }

    handleDialogClick(event) {
      if (event.target === this.dialog || event.target.dataset.storyOverlay !== undefined) {
        this.close();
      }
    }

    handleKeydown(event) {
      switch (event.key) {
        case 'Escape':
          this.close();
          break;
        case 'ArrowLeft':
          this.previous();
          break;
        case 'ArrowRight':
          this.next();
          break;
        case ' ':
          event.preventDefault();

          if (!this.video) return;

          if (this.video.paused) {
            this.video.play().catch(() => {});
          } else {
            this.video.pause();
          }
          break;
        default:
          break;
      }
    }

    handleSwipe(event) {
      const endX = event.changedTouches[0].clientX;
      const endY = event.changedTouches[0].clientY;
      const deltaX = endX - this.touchStartX;
      const deltaY = endY - this.touchStartY;

      if (Math.abs(deltaX) < 45 || Math.abs(deltaX) < Math.abs(deltaY)) {
        return;
      }

      if (deltaX < 0) {
        this.next();
      } else {
        this.previous();
      }
    }
  }

  customElements.define('product-video-stories', ProductVideoStories);
}